import { useEffect, useRef, useState } from 'react';
import {
  STATUS_MIN_DISPLAY_MS,
  advanceStatusQueue,
  arriveStatus,
  clearPendingStatus,
  initStatusQueue,
  type StatusQueueEntry,
  type StatusQueueState,
} from './statusQueue';

/**
 * Holds a running row's status label (+ its page chip) on screen for at
 * least STATUS_MIN_DISPLAY_MS before advancing to whatever arrived during
 * the hold — see statusQueue.ts for the queueing rules this wraps in real
 * timers. `latest === null` (the row left running state) drops anything
 * held back immediately and the hook reports nothing.
 */
export function useStatusQueue(latest: StatusQueueEntry | null): StatusQueueEntry | null {
  const [state, setState] = useState<StatusQueueState | null>(() =>
    latest ? initStatusQueue(latest, Date.now()) : null,
  );
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // `latest` is a fresh object every render, so this runs often — but
  // arriveStatus/clearPendingStatus hand back the same object when nothing
  // actually changed, and React skips the re-render (and the timer effect
  // below) when setState's updater returns the previous reference.
  useEffect(() => {
    if (!latest) {
      clearTimer();
      setState((prev) => (prev ? clearPendingStatus(prev) : prev));
      return undefined;
    }
    setState((prev) => (prev ? arriveStatus(prev, latest, Date.now()) : initStatusQueue(latest, Date.now())));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest]);

  // (Re)arm the promote-the-pending-entry timer whenever there is one queued.
  useEffect(() => {
    clearTimer();
    if (!latest || !state?.pending) return undefined;
    const delay = Math.max(0, STATUS_MIN_DISPLAY_MS - (Date.now() - state.shownAt));
    timerRef.current = window.setTimeout(() => {
      setState((prev) => (prev ? advanceStatusQueue(prev, Date.now()) : prev));
    }, delay);
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, latest]);

  useEffect(() => clearTimer, []);

  return latest ? state?.shown ?? latest : null;
}
